if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
      this.handlePageShow = this.restoreCompareState.bind(this);
    }

    connectedCallback() {
      if (this.compareToggle) window.addEventListener('pageshow', this.handlePageShow);
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.handlePageShow);
      this.mobileSizeObserver?.disconnect();
      this.mobileSizeMedia?.removeEventListener('change', this.handleMobileSizeMedia);
    }

    restoreCompareState() {
      const hasCompareLink = new URLSearchParams(window.location.search).has('compare');
      const storedToggle = theme.storageUtil.get('compare-toggle', true);
      const isChecked = hasCompareLink
        || (storedToggle !== null ? storedToggle : this.productsGrid.dataset.compare === 'true');

      // Browsers can restore checkbox values independently when navigating back.
      this.toggleCompare(null, isChecked, false);
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));
        if (this.querySelector('[name="products-mobile-size"]')) {
          const storedSize = Shopify.designMode ? null : theme.storageUtil.get('products-card-size-mobile');
          this.mobileCardSize = ['small', 'large'].includes(storedSize)
            ? storedSize : this.dataset.cardSizeMobile;
          this.mobileSizeMedia = window.matchMedia('(max-width: 599.98px)');
          this.handleMobileSizeMedia = this.applyMobileCardSize.bind(this);
          this.mobileSizeMedia.addEventListener('change', this.handleMobileSizeMedia);
          this.applyMobileCardSize();
          this.mobileSizeObserver = new MutationObserver(() => this.applyMobileCardSize());
          this.mobileSizeObserver.observe(this.productsGrid.querySelector('.main-products-grid__results'), { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        }

        // Set the layout to the layout in localstorage
        const layout = theme.storageUtil.get('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) {
            toggle.click();
            this.handleLayoutChange(null, layout);
          }
        }
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const hasCompareLink = new URLSearchParams(window.location.search).has('compare');
        const isChecked = hasCompareLink || theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, !hasCompareLink);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else {
        /* Original no-switch behavior; compare now follows the grid eligibility.
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
        */
        this.toggleCompare(null, this.productsGrid.dataset.compare === 'true', false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      document.dispatchEvent(new CustomEvent('on:compare-products:updated'));

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        const productsList = document.querySelector('.main-products-grid__results .grid[data-filters-open-classes]');

        productsList.className = this.filtersOpen
          ? productsList.dataset.filtersOpenClasses
          : productsList.dataset.filtersClosedClasses;
        this.applyMobileCardSize();
      } else {
        this.filtersComponent.open();
      }
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      if (evt?.target.name === 'products-mobile-size') {
        this.mobileCardSize = evt.target.value;
        if (!Shopify.designMode) theme.storageUtil.set('products-card-size-mobile', this.mobileCardSize);
        this.querySelectorAll('[name="products-layout"]').forEach((input) => {
          input.checked = input.value === 'grid';
        });
        this.productsGrid.dataset.layout = 'grid';
        theme.storageUtil.set('products-layout', 'grid');
      } else {
        this.productsGrid.dataset.layout = layout || evt.target.value;
        if (!layout) theme.storageUtil.set('products-layout', evt.target.value);
      }
      this.applyMobileCardSize();
    }

    applyMobileCardSize() {
      if (!this.mobileCardSize) return;
      const cardSize = this.mobileSizeMedia.matches ? this.mobileCardSize : this.dataset.cardSizeMobile;
      const productsList = this.productsGrid.querySelector('.main-products-grid__results .grid[data-filters-open-classes]');
      if (productsList) {
        const classes = [...productsList.classList].filter((name) =>
          !['grid-cols-1', 'grid-cols-2', 'small-cards-mobile'].includes(name));
        classes.push(...(cardSize === 'small' ? ['grid-cols-2', 'small-cards-mobile'] : ['grid-cols-1']));
        const className = classes.join(' ');
        if (productsList.className !== className) productsList.className = className;
      }
      this.querySelectorAll('[name="products-mobile-size"]').forEach((input) => {
        input.checked = this.productsGrid.dataset.layout === 'grid' && input.value === this.mobileCardSize;
      });
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
